import { AsyncStore } from "@me8eon/async_storage";
import { CryptoMetadata } from "@enterprise_search/storage_crypto";
import { CryptoClient } from "@me8eon/azure_async_storage";
import { ErrorsOr, isErrors } from "@me8eon/errors";
import { JwtTokenFn } from "@me8eon/authentication";

export type CryptoStoreConfig<Id> = {
    jwtTokenFn: JwtTokenFn;
    cryptoClient: CryptoClient;
    rawStore: AsyncStore<Id, CryptoMetadata>;
    metaCache: Map<Id, Promise<ErrorsOr<CryptoMetadata>>>;
    secretCache: Map<Id, Promise<ErrorsOr<string>>>;
};

export type CryptoStore<Id> = {
    secret: (id: Id) => Promise<ErrorsOr<string>>;
    metaData: (id: Id) => Promise<ErrorsOr<CryptoMetadata>>;
};

// single-flight: cache the *pending* promise; on error, evict so next time can retry
function singleFlight<Id, T>(
    cache: Map<Id, Promise<ErrorsOr<T>>>,
    id: Id,
    factory: () => Promise<ErrorsOr<T>>
): Promise<ErrorsOr<T>> {
    const existing = cache.get(id);
    if (existing) return existing;
    const p = (async () => {
        const res = await factory();
        if (isErrors(res)) cache.delete(id); // let callers retry after an error
        return res;
    })().catch(err => {
        // Unexpected throw (not an ErrorsOr). Ensure cache eviction.
        cache.delete(id);
        throw err;
    });
    cache.set(id, p);
    return p;
}

export function cryptoStoreMetaData<Id>(
    config: CryptoStoreConfig<Id>
): (id: Id) => Promise<ErrorsOr<CryptoMetadata>> {
    const { metaCache, secretCache, rawStore, cryptoClient, jwtTokenFn } = config;

    return async function metaData(id: Id): Promise<ErrorsOr<CryptoMetadata>> {
        const token = await jwtTokenFn();
        if (!token) return { errors: ["Cannot get metadata as not logged in"] };

        return singleFlight(metaCache, id, async () => {
            // Try store first
            const fromStore = await rawStore.get(id, true); // undefined if not present
            if (isErrors(fromStore)) return fromStore;

            if (fromStore.value != undefined) { // catches null as well
                // Populate cache with store value (single-flight ensures this is cached)
                return { value: fromStore.value };
            }

            // Need to create a new one
            const created = await cryptoClient.initialMeta(token);
            if (isErrors(created)) return created;

            // Pair the secret cache for this id, since initialMeta yields both

            // Persist the metadata as it wasn't there
            const storeRes = await rawStore.store(id, created.value.metadata);
            if (isErrors(storeRes)) {
                // Failed to persist, evict metaCache and secretCache so next time can retry
                //Note we cannot use a non persisted secret, because next time it will not be readable
                metaCache.delete(id);
                secretCache.delete(id);
                return storeRes;
            }
            secretCache.set(id, Promise.resolve({ value: created.value.secret }));
            return { value: created.value.metadata };
        });
    };
}

export function cryptoStoreSecret<Id>(
    config: CryptoStoreConfig<Id>
): (id: Id) => Promise<ErrorsOr<string>> {
    const { secretCache, rawStore, cryptoClient, jwtTokenFn } = config;
    const metaGet = cryptoStoreMetaData(config);

    return async function secret(id: Id): Promise<ErrorsOr<string>> {
        const token = await jwtTokenFn();
        if (!token) return { errors: ["Cannot get secret as not logged in"] };

        return singleFlight(secretCache, id, async () => {
            const meta = await metaGet(id);
            if (isErrors(meta)) return meta;

            const secretRes = await cryptoClient.secretFor(token, meta.value);
            if (isErrors(secretRes)) return secretRes;

            // Ensure metadata is persisted if it wasn't there
            const existingMeta = await rawStore.get(id, true);
            if (isErrors(existingMeta)) return existingMeta;

            if (existingMeta.value === undefined) {
                const storeRes = await rawStore.store(id, meta.value);
                if (isErrors(storeRes)) return storeRes; // let caller retry persist next time
            }

            return { value: secretRes.value };
        });
    };
}

export function createCryptoStore<Id>(config: CryptoStoreConfig<Id>): CryptoStore<Id> {
    return {
        secret: cryptoStoreSecret(config),
        metaData: cryptoStoreMetaData(config),
    }
}